import React from 'react';
import styled from 'styled-components';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import BuildIcon from '@mui/icons-material/Build';
import SellIcon from '@mui/icons-material/Sell';
import TruncatedText from '@spark/ui-components/components/TanStackTable/components/TruncatedText';
import Tooltip from '@spark/ui-components/components/Tooltip';
import InlineTextCell from '../../../Deposito/Listagem/components/InlineTextCell';
import { formatarDataBr, juntarDataHoraBr } from '../utils/dataBr';
import { montarMensagemGestor } from '../utils/mensagemGestor';
import { logoDaTecnologia } from '../utils/tecnologiaRastreador';
import logoHavan from '../assets/plotagem/havan.png';
import logoTransben from '../assets/plotagem/transben.png';

const EDITABLE_COLUMN_WIDTH = 200;

const renderText = (maxLength = 18) => ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={maxLength} />;

// Saída CDH: a planilha traz só o dia; a hora da saída é o término do carregamento.
export const descreverSaidaCdh = registro => juntarDataHoraBr(registro.saida_cdh, registro.termino_carregamento);

// Chegada do veículo para carregar ("H.CHEG VEICULO"): no dia do carregamento (o mesmo da mensagem ao gestor).
const diaDoCarregamento = registro => registro.data_programacao || registro.saida_cdh;
export const descreverChegadaVeiculo = registro => (registro.inicio_carregamento ? juntarDataHoraBr(diaDoCarregamento(registro), registro.inicio_carregamento) : '');

// Ordenação de data + hora: ISO "aaaa-mm-dd hh:mm" ordena certo como texto.
const chaveDataHora = (data, hora) => `${String(data || '').slice(0, 10)} ${hora || ''}`;

const renderSaidaCdh = () => ({ row }) => {
  const registro = row.original;
  const texto = descreverSaidaCdh(registro);
  if (!texto) return '-';
  const carregamento = registro.inicio_carregamento && registro.termino_carregamento
    ? `Carregamento das ${registro.inicio_carregamento} às ${registro.termino_carregamento}`
    : 'Horário de carregamento não informado';
  return <DataCelula title={carregamento}>{texto}</DataCelula>;
};

const renderData = () => ({ getValue }) => <DataCelula>{formatarDataBr(getValue()) || '-'}</DataCelula>;

const renderChegadaVeiculo = () => ({ row }) => <DataCelula>{descreverChegadaVeiculo(row.original) || '-'}</DataCelula>;

// DDD + número, sem o 55. Celular antigo com 8 dígitos (começa com 6 a 9) ganha o 9º dígito: sem ele o WhatsApp não acha a conversa.
export const normalizarTelefoneBr = phone => {
  const digits = String(phone || '').replace(/\D/g, '').replace(/^0+/, '').replace(/^55(?=\d{10,11}$)/, '');
  if (digits.length === 10 && /^[6-9]$/.test(digits[2])) return `${digits.slice(0, 2)}9${digits.slice(2)}`;
  return digits;
};

const normalizeWhatsappPhone = phone => {
  const digits = normalizarTelefoneBr(phone);
  if (!digits) return '';
  return digits.length === 10 || digits.length === 11 ? `55${digits}` : digits;
};

const formatarTelefone = phone => {
  const digits = normalizarTelefoneBr(phone);
  if (digits.length === 11) return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  if (digits.length === 10) return digits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  return String(phone || '').trim();
};

// De onde veio o número do gestor: o secundário de Pessoas é o WhatsApp corporativo; os outros podem ser pessoais.
const ORIGEM_TELEFONE_GESTOR = {
  corporativo: 'telefone corporativo',
  pessoas: 'telefone principal de Pessoas (pode ser pessoal)',
  usuario: 'telefone do cadastro de usuário (pode ser pessoal)'
};

// O TMS grava o nome em maiúsculas: na célula vai só o primeiro nome; o completo fica no tooltip.
const capitalizar = nome => String(nome || '').toLowerCase().replace(/(^|\s)\S/g, letra => letra.toUpperCase());
const primeiroNome = nome => capitalizar(String(nome || '').trim().split(/\s+/)[0]);

// Gestor = responsável (FUN_CODIGO_RESPONSAVEL) do cavalo no TMS: é a ele que se fala sobre a carga (não ao motorista).
export const descreverGestor = registro => {
  const nome = String(registro.gestor_nome || '').trim();
  const telefone = String(registro.gestor_telefone || '').trim();
  const numero = normalizeWhatsappPhone(telefone);
  if (!nome && !telefone) {
    return { rotulo: 'Sem gestor', titulo: registro.placa_cavalo ? 'Cavalo sem responsável (gestor) cadastrado no TMS.' : 'Informe o cavalo para ver o gestor.', url: '' };
  }
  const quem = nome ? capitalizar(nome) : 'Gestor';
  const origem = ORIGEM_TELEFONE_GESTOR[registro.gestor_telefone_origem];
  return {
    rotulo: nome ? primeiroNome(nome) : formatarTelefone(telefone),
    titulo: numero
      ? `Gestor: ${quem} · ${formatarTelefone(telefone)}${origem ? ` (${origem})` : ''}. Clique para abrir o WhatsApp com a mensagem da programação.`
      : `Gestor: ${quem} · sem telefone no TMS.`,
    url: numero ? `https://wa.me/${numero}?text=${encodeURIComponent(montarMensagemGestor(registro))}` : '',
    corporativo: registro.gestor_telefone_origem === 'corporativo'
  };
};

// Abrir o WhatsApp do gestor marca a linha como "gestor avisado" (verde forte); a marca é gravada com quem e quando.
const renderGestorCell = inlineEditConfig => ({ row }) => {
  const gestor = descreverGestor(row.original);

  return (
    <Tooltip dir="top" text={gestor.titulo}>
      <span className="d-inline-block">
        <GestorButton
          type="button"
          disabled={!gestor.url}
          $pessoal={Boolean(gestor.url) && !gestor.corporativo}
          aria-label={gestor.titulo}
          onClick={event => {
            event.stopPropagation();
            if (!gestor.url) return;
            window.open(gestor.url, '_blank', 'noopener');
            inlineEditConfig?.marcarGestorAvisado?.(row.original);
          }}
        >
          <WhatsAppIcon fontSize="inherit" />
          <span>{gestor.rotulo}</span>
        </GestorButton>
      </span>
    </Tooltip>
  );
};

// Consultas que não resultaram em pesquisa na VX: o usuário precisa continuar (criar ou renovar).
// Inclui a pesquisa cancelada ou invalidada por troca de motorista/veículo: a carga precisa de nova consulta.
const ACOES_VX_SEM_PESQUISA = ['consulta-nao-encontrada', 'consulta-encontrada-sem-laudo', 'erro-vx', 'pesquisa-cancelada', 'pesquisa-invalidada'];
const ACOES_VX_INVALIDADAS = ['pesquisa-cancelada', 'pesquisa-invalidada'];
// Pesquisa existe na VX mas ainda não concluiu: o clique reconsulta e traz o laudo quando ficar pronto.
const ACOES_VX_EM_ANDAMENTO = ['pesquisa-criada', 'pesquisa-em-andamento', 'pesquisa-renovada', 'acompanhamento'];

const normalizarPlaca = placa => String(placa || '').replace(/[^a-z0-9]/gi, '').toUpperCase();

const lerPlacasConsultadas = registro => {
  try {
    const placas = JSON.parse(registro.vx_placas || '[]');
    return Array.isArray(placas) ? placas.map(normalizarPlaca).filter(Boolean) : [];
  } catch {
    return [];
  }
};

// Mesma regra da busca na VX: o conjunto atual precisa estar contido nas placas consultadas.
const isConjuntoDivergente = registro => {
  const consultadas = lerPlacasConsultadas(registro);
  if (consultadas.length === 0) return false;
  return [registro.placa_cavalo, registro.placa_carreta_1, registro.placa_carreta_2, registro.placa_carreta_3]
    .map(normalizarPlaca)
    .filter(Boolean)
    .some(placa => !consultadas.includes(placa));
};

export const getVxEstado = registro => {
  if (!registro.vx_consultada) return 'consultar';
  if (String(registro.vx_status || '').toLowerCase() === 'erro') return 'consultar';
  if (ACOES_VX_SEM_PESQUISA.includes(registro.vx_acao_executada)) return 'consultar';
  if (isConjuntoDivergente(registro)) return 'consultar';
  if (ACOES_VX_EM_ANDAMENTO.includes(registro.vx_acao_executada)) return 'andamento';
  const validade = Date.parse(registro.vx_validade);
  return Number.isNaN(validade) || validade >= Date.now() ? 'valida' : 'consultar';
};

const getVxLabel = (registro, estado) => {
  if (estado === 'consultar') return 'Consultar VX';
  if (estado === 'andamento') return registro.vx_status || 'Pesquisa em análise';
  return registro.vx_status || 'Consultada';
};

const VX_TITULOS = {
  consultar: 'Consultar dados da carga na VX',
  andamento: 'Pesquisa em andamento na VX. Clique para atualizar o status e buscar o laudo.',
  valida: 'Esta carga já possui uma consulta VX válida.'
};

const hasVxAlert = registro => (
  String(registro.vx_status || '').toLowerCase() === 'erro' &&
  Boolean(registro.vx_mensagem)
);

const renderVxCell = vxConfig => ({ row }) => {
  const registro = row.original;
  const estado = getVxEstado(registro);
  const consultada = estado === 'valida';
  const alerta = hasVxAlert(registro);
  const laudoUrl = registro.vx_laudo_url;
  const consultando = vxConfig?.consultandoId != null && vxConfig.consultandoId === registro.id;
  const baixando = vxConfig?.baixandoLaudoId != null && vxConfig.baixandoLaudoId === registro.id;
  const laudoDeOutroConjunto = Boolean(laudoUrl) && isConjuntoDivergente(registro);

  if (registro.cancelado) {
    return <VxCell><VxButton type="button" disabled $consultada title="Carga cancelada: não precisa de consulta VX.">Cancelada</VxButton></VxCell>;
  }

  return (
    <VxCell>
      <VxButton
        type="button"
        disabled={consultada || consultando}
        aria-busy={consultando}
        title={consultada
          ? (registro.vx_mensagem || VX_TITULOS.valida)
          : estado === 'andamento' ? `${getVxLabel(registro, estado)}. ${VX_TITULOS.andamento}`
            : ACOES_VX_INVALIDADAS.includes(registro.vx_acao_executada) && registro.vx_mensagem ? registro.vx_mensagem : VX_TITULOS[estado]}
        $consultada={consultada}
        $andamento={estado === 'andamento'}
        onClick={event => {
          event.stopPropagation();
          if (!consultada) vxConfig?.onConsultar?.(registro);
        }}
      >
        {estado === 'andamento' && !consultando ? <RefreshIcon fontSize="inherit" /> : null}
        <VxTexto>{consultando ? 'Consultando...' : getVxLabel(registro, estado)}</VxTexto>
      </VxButton>
      {laudoUrl ? (
        <Tooltip dir="top" text={laudoDeOutroConjunto ? `Laudo do conjunto consultado anteriormente (${lerPlacasConsultadas(registro).join(' / ')}). Consulte a VX para o conjunto atual.` : 'Baixar o PDF do laudo VX'}>
          <VxLaudoButton
            type="button"
            aria-label="Baixar laudo VX"
            disabled={baixando}
            onClick={event => {
              event.stopPropagation();
              vxConfig?.onBaixarLaudo?.(registro);
            }}
          >
            <PictureAsPdfIcon fontSize="inherit" />
            {baixando ? 'Baixando...' : 'Baixar laudo'}
          </VxLaudoButton>
        </Tooltip>
      ) : null}
      {alerta ? (
        <Tooltip dir="top" text={registro.vx_mensagem}>
          <VxAlertIcon
            role="button"
            tabIndex={0}
            aria-label="Alerta da consulta VX"
            title="Revisar dados e reenviar a consulta VX"
            onClick={event => { event.stopPropagation(); vxConfig?.onAbrirDocumentos?.(registro); }}
            onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); vxConfig?.onAbrirDocumentos?.(registro); } }}
          >
            <WarningAmberIcon fontSize="inherit" />
          </VxAlertIcon>
        </Tooltip>
      ) : null}
    </VxCell>
  );
};

// Mostra antes do clique o que o vínculo do motorista no TMS vai preencher na linha.
export const descreverMotorista = opcao => {
  if (opcao.fora_tms) return `${opcao.nome_motorista || '-'}${opcao.cpf_motorista ? ` · ${opcao.cpf_motorista}` : ''} · ${opcao.enviado_tms ? 'enviado ao TMS · aguardando sincronização' : 'fora do TMS (informado antes)'}`;
  const base = `${opcao.nome_motorista || '-'}${opcao.cpf_motorista ? ` · ${opcao.cpf_motorista}` : ''}`;
  if (opcao.cavalos_vinculados > 1) return `${base} · ${opcao.cavalos_vinculados} cavalos vinculados — escolha a frota`;
  if (opcao.cavalos_vinculados !== 1 || !opcao.placa_cavalo) return base;
  const carretas = (opcao.carretas || []).filter(Boolean);
  const frota = opcao.numero_frota ? `Frota ${opcao.numero_frota} · ` : '';
  return `${base} · ${frota}${opcao.placa_cavalo}${carretas.length ? ` + ${carretas.length} carreta(s)` : ''}`;
};

// No campo Frota o número da frota vem primeiro (veículo interno); sem frota, a placa identifica o terceiro.
export const descreverVeiculo = (opcao, field) => {
  if (opcao.fora_tms) return `${opcao.placa_cavalo || '-'} · ${opcao.enviado_tms ? 'enviada ao TMS · aguardando sincronização' : 'fora do TMS (informada antes)'}`;
  const origem = opcao.proprio ? 'Próprio' : 'Terceiro';
  // Nas carretas o conjunto não se aplica; no cavalo/frota ele mostra o que vai preencher Carreta 1, 2 e 3.
  const conjunto = field !== 'numero_frota' && field !== 'placa_cavalo' ? [] : (opcao.carretas || []).filter(Boolean);
  const sufixoConjunto = conjunto.length ? ` · Conjunto: ${conjunto.join(' / ')}` : '';
  if (field === 'numero_frota' && opcao.numero_frota) return `${opcao.numero_frota} · ${opcao.placa_cavalo || '-'} · ${origem}${sufixoConjunto}`;
  return `${opcao.placa_cavalo || '-'}${opcao.numero_frota ? ` · ${opcao.numero_frota}` : ''} · ${origem}${sufixoConjunto}`;
};

// Tecnologia de rastreamento ao lado do veículo: logo pequena; sem logo conhecida, o nome numa pílula discreta.
export const SeloTecnologia = ({ tecnologia }) => {
  if (!tecnologia) return null;
  const logo = logoDaTecnologia(tecnologia);
  if (logo) return <LogoSelo src={logo} alt={`Rastreador ${tecnologia}`} title={`Rastreador: ${tecnologia}`} />;
  return <Selo title={`Rastreador: ${tecnologia}`} aria-label={`Rastreador ${tecnologia}`}>{tecnologia}</Selo>;
};

// Plotagem do veículo no TMS: logo da Havan/Transben; Patriota em quadrado amarelo e sem pintura em quadrado cinza.
export const PLOTAGENS = {
  havan: { nome: 'Havan', logo: logoHavan },
  transben: { nome: 'Transben', logo: logoTransben },
  patriota: { nome: 'Patriota', cor: '#facc15' },
  sem_pintura: { nome: 'Sem pintura', cor: '#cbd5e1' }
};

export const SeloPlotagem = ({ plotagem }) => {
  const config = PLOTAGENS[plotagem];
  if (!config) return null;
  const rotulo = `Plotagem ${config.nome}`;
  if (config.logo) return <LogoSelo src={config.logo} alt={rotulo} title={`Plotagem: ${config.nome}`} />;
  return <QuadradoPlotagem role="img" aria-label={rotulo} title={`Plotagem: ${config.nome}`} $cor={config.cor} />;
};

// Carroceria da carreta (TMS): só nas sugestões das carretas, onde escolher sider ou baú importa.
export const CARROCERIAS = {
  sider: { nome: 'Sider', descricao: 'Carreta sider (lonada, abre pela lateral).', cor: '#0f766e', fundo: '#ccfbf1', borda: '#5eead4' },
  bau: { nome: 'Baú', descricao: 'Carreta baú (fechada).', cor: '#334155', fundo: '#e2e8f0', borda: '#94a3b8' }
};

export const SeloCarroceria = ({ carroceria }) => {
  const config = CARROCERIAS[carroceria];
  if (!config) return null;
  return (
    <SeloTexto title={`Carroceria: ${config.nome}`} $cor={config.cor} $fundo={config.fundo} $borda={config.borda}>
      {config.nome}
    </SeloTexto>
  );
};

export const renderSugestaoVeiculo = (opcao, field) => (
  <SugestaoVeiculo>
    <span>{descreverVeiculo(opcao, field)}</span>
    <Selos>
      {field.startsWith('placa_carreta_') ? <SeloCarroceria carroceria={opcao.carroceria} /> : null}
      <TagStatusPatio status={opcao.status_patio} />
      <SeloPlotagem plotagem={opcao.plotagem} />
      <SeloTecnologia tecnologia={opcao.tecnologia_rastreador} />
    </Selos>
  </SugestaoVeiculo>
);

// Campo da linha que diz se o valor da célula está fora do TMS.
const CAMPO_FORA_TMS = {
  nome_motorista: 'motorista_fora_tms',
  placa_cavalo: 'placa_cavalo_fora_tms',
  placa_carreta_1: 'placa_carreta_1_fora_tms',
  placa_carreta_2: 'placa_carreta_2_fora_tms',
  placa_carreta_3: 'placa_carreta_3_fora_tms'
};

export const registroForaDoTms = registro => Object.values(CAMPO_FORA_TMS).some(campo => Boolean(registro?.[campo]));

const PLACA_VALIDA = /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/;

// Não achou no TMS: motorista vai para o formulário (CPF obrigatório); placa válida é usada direto. Frota não tem essa saída.
export const acaoForaDoTms = (field, termo) => {
  const texto = String(termo || '').trim();
  if (!texto || !CAMPO_FORA_TMS[field]) return null;
  if (field === 'nome_motorista') return { label: `+ Usar "${texto}" fora do TMS (informar CPF)` };
  const placa = normalizarPlaca(texto);
  if (!PLACA_VALIDA.test(placa)) {
    return { label: 'Digite a placa completa (ABC1234 ou ABC1D23) para usar fora do TMS', disabled: true };
  }
  return { label: `+ Usar a placa ${placa} fora do TMS` };
};

const TOOLTIP_FORA_TMS = {
  motorista: 'Motorista não cadastrado no TMS (CPF não encontrado). A programação e a consulta VX seguem normalmente; quando o CPF aparecer no TMS, a linha passa a usar o cadastro de lá.',
  placa: 'Placa não cadastrada no TMS. A programação e a consulta VX seguem normalmente; quando a placa aparecer no TMS, a linha passa a usar o cadastro de lá.'
};

// Carreta: "FROTA / PLACA". Sem frota, ou frota igual à placa (convenção de terceiro no TMS), só a placa.
export const descreverCarreta = (registro, field) => {
  const placa = String(registro?.[field] || '').trim();
  const frota = String(registro?.[`frota_${field.replace('placa_', '')}`] || '').trim();
  if (!placa) return '';
  return frota && frota.toUpperCase() !== placa.toUpperCase() ? `${frota} / ${placa}` : placa;
};

// Lixeira no editor de cada célula: o gestor apaga motorista que não vai ou veículo que vai trocar.
const ROTULO_APAGAR = {
  nome_motorista: 'Remover o motorista da carga',
  numero_frota: 'Remover o cavalo (frota e placa); as carretas ficam',
  placa_cavalo: 'Remover o cavalo (frota e placa); as carretas ficam'
};

const renderEditableCell = (field, maxLength, displayMaxLength, inlineEditConfig) => ({ getValue, row }) => (
  <CelulaEditavel>
    <InlineTextCell
      value={field.startsWith('placa_carreta_') ? descreverCarreta(row.original, field) : getValue()}
      maxLength={maxLength}
      displayMaxLength={displayMaxLength}
      isEditing={inlineEditConfig?.editingCellKey === `${row.original.id}:${field}`}
      isSaving={inlineEditConfig?.savingCellKey === `${row.original.id}:${field}`}
      editingValue={inlineEditConfig?.editingInitialValue || ''}
      usePopover
      selectionRequired
      emptySuggestionsText={field === 'nome_motorista' ? 'Nenhum motorista encontrado no TMS.' : 'Nenhum veículo com essa frota ou placa no TMS.'}
      onSearch={value => inlineEditConfig?.searchOptions?.(field, row.original, value)}
      onSelectSuggestion={opcao => inlineEditConfig?.selectOption?.(field, row.original, opcao)}
      renderSuggestion={opcao => (field === 'nome_motorista' ? descreverMotorista(opcao) : renderSugestaoVeiculo(opcao, field))}
      initialPopoverPosition={inlineEditConfig?.popoverPosition}
      onStartEdit={({ popoverPosition } = {}) => inlineEditConfig?.startEdit?.(field, row.original, popoverPosition)}
      onCommit={value => inlineEditConfig?.commitEdit?.({ registro: row.original, campo: field, value })}
      onCancel={() => inlineEditConfig?.cancelEdit?.()}
      onClear={inlineEditConfig?.clearField ? () => inlineEditConfig.clearField(field, row.original) : null}
      clearLabel={ROTULO_APAGAR[field] || `Remover a carreta ${field.slice(-1)} do conjunto`}
      extraAction={inlineEditConfig?.useForaTms ? termo => {
        const acao = acaoForaDoTms(field, termo);
        return acao ? { ...acao, onSelect: valor => inlineEditConfig.useForaTms(field, row.original, valor) } : null;
      } : null}
      actionsAtEnd
      adornment={selosDaCelula(field, row.original, inlineEditConfig)}
    />
  </CelulaEditavel>
);

// Selo "fora do TMS" e lápis encostados no fim da célula: o lápis fica alinhado na coluna.
const selosDaCelula = (field, registro, inlineEditConfig) => (
  registro[CAMPO_FORA_TMS[field]] ? renderSeloForaTms(field, registro, inlineEditConfig?.cadastroTms) : null
);

// Status de pátio da Rabbot (GestaoFrotas) do veículo na busca; mesma numeração da tela de Disponibilidade.
// Ícone colorido na busca de veículo; o nome aparece no tooltip.
export const STATUS_PATIO = {
  1: { rotulo: 'Disponível', Icone: CheckCircleIcon, cor: '#15803d', fundo: '#dcfce7' },
  2: { rotulo: 'Indisponível', Icone: BlockIcon, cor: '#b91c1c', fundo: '#fee2e2' },
  3: { rotulo: 'Em rota', Icone: LocalShippingIcon, cor: '#1d4ed8', fundo: '#dbeafe' },
  4: { rotulo: 'Problemas identificados', Icone: ReportProblemIcon, cor: '#b45309', fundo: '#fef3c7' },
  5: { rotulo: 'Manutenção', Icone: BuildIcon, cor: '#c2410c', fundo: '#ffedd5' },
  6: { rotulo: 'Em venda', Icone: SellIcon, cor: '#475569', fundo: '#e2e8f0' }
};

export const TagStatusPatio = ({ status }) => {
  const config = STATUS_PATIO[status];
  if (!config) return null;
  const texto = `Status Rabbot: ${config.rotulo}`;
  return (
    <Tooltip dir="top" text={texto}>
      <IconePatio role="img" aria-label={texto} $cor={config.cor} $fundo={config.fundo}>
        <config.Icone aria-hidden="true" />
      </IconePatio>
    </Tooltip>
  );
};

// Observação livre do operador: texto sem busca (não é cadastro do TMS); a lixeira só aparece quando há observação.
const renderObservacaoCell = inlineEditConfig => ({ getValue, row }) => {
  const field = 'observacao_operador';
  return (
    <InlineTextCell
      value={getValue() || ''}
      maxLength={500}
      displayMaxLength={30}
      isEditing={inlineEditConfig?.editingCellKey === `${row.original.id}:${field}`}
      isSaving={inlineEditConfig?.savingCellKey === `${row.original.id}:${field}`}
      editingValue={inlineEditConfig?.editingInitialValue || ''}
      usePopover
      initialPopoverPosition={inlineEditConfig?.popoverPosition}
      onStartEdit={({ popoverPosition } = {}) => inlineEditConfig?.startEdit?.(field, row.original, popoverPosition)}
      onCommit={value => inlineEditConfig?.commitEdit?.({ registro: row.original, campo: field, value })}
      onCancel={() => inlineEditConfig?.cancelEdit?.()}
      onClear={getValue() && inlineEditConfig?.clearField ? () => inlineEditConfig.clearField(field, row.original) : null}
      clearLabel="Apagar a observação"
      actionsAtEnd
    />
  );
};

// Já cadastrado no TMS por esta tela: some o convite para cadastrar (não duplica) e explica a espera da sincronização.
const descreverEnvioTms = (field, ambiente) => {
  const quem = field === 'nome_motorista' ? 'Motorista' : 'Placa';
  const local = ambiente === 'producao' ? 'no TMS' : `no TMS de ${ambiente === 'homologacao' ? 'homologação' : ambiente}`;
  return ambiente === 'producao'
    ? `${quem} já cadastrado ${local} por esta tela. A linha passa a usar o cadastro do TMS depois da sincronização do dia seguinte.`
    : `${quem} já cadastrado ${local} por esta tela. A homologação não vai para o datalake: aqui a linha continua fora do TMS.`;
};

const renderSeloForaTms = (field, registro, cadastroTms) => {
  const envio = registro[`${CAMPO_FORA_TMS[field]}`.replace('_fora_tms', '_envio_tms')];
  const tooltipBase = field === 'nome_motorista' ? TOOLTIP_FORA_TMS.motorista : TOOLTIP_FORA_TMS.placa;
  if (envio) {
    return (
      <Tooltip dir="top" text={descreverEnvioTms(field, envio)}>
        <EnviadoTmsBadge>Enviado ao TMS</EnviadoTmsBadge>
      </Tooltip>
    );
  }
  if (cadastroTms?.habilitado && cadastroTms.abrir) {
    const titulo = `${tooltipBase} Clique para cadastrar no TMS.`;
    return (
      <Tooltip dir="top" text={titulo}>
        <ForaTmsBadge
          as="button"
          type="button"
          aria-label={field === 'nome_motorista' ? 'Cadastrar motorista no TMS' : `Cadastrar a placa ${registro[field] || ''} no TMS`}
          onClick={event => { event.stopPropagation(); cadastroTms.abrir(field, registro); }}
          $clicavel
        >
          Fora do TMS
        </ForaTmsBadge>
      </Tooltip>
    );
  }
  return (
    <Tooltip dir="top" text={tooltipBase}>
      <ForaTmsBadge>Fora do TMS</ForaTmsBadge>
    </Tooltip>
  );
};

export const COR_LINHA_TERCEIRO = '#fb923c';
export const COR_LINHA_PROTOCOLO = '#e9d5ff';
// Gestor avisado pelo WhatsApp nesta tela: verde forte, acima de protocolo e terceiro.
export const COR_LINHA_GESTOR_AVISADO = '#4ade80';
// Consulta VX válida (tudo ok): verde bem clarinho, abaixo das marcações do operador.
export const COR_LINHA_VX_VALIDA = '#ecfdf3';
// Linha incompleta (sem motorista ou sem veículo): vermelho meio fraco, a cor de menor prioridade.
export const COR_LINHA_INCOMPLETA = '#fecaca';

const preenchido = valor => String(valor ?? '').trim() !== '';

/**
 * Carga ainda incompleta, pelo que a tela mostra (programação ou carga do TMS): falta o motorista ou o veículo
 * (veículo = Frota ou Placa cavalo, basta um). Com os dois informados a linha volta ao normal.
 * Cancelada não conta (não vai sair).
 */
export const descreverPendencias = registro => {
  if (!registro || registro.cancelado) return [];
  return [
    !preenchido(registro.nome_motorista || registro.nome) ? 'sem motorista' : null,
    !preenchido(registro.placa_cavalo) && !preenchido(registro.numero_frota) ? 'sem frota/cavalo' : null
  ].filter(Boolean);
};
export const registroIncompleto = registro => descreverPendencias(registro).length > 0;
export const COR_LINHA_CANCELADA = '#eceff3';

// Marcação da programação definitiva no CT: o tooltip explica o que a definitiva mudou.
const SITUACOES_DEFINITIVA = {
  nova: { rotulo: 'Nova', descricao: 'Carga incluída pela programação definitiva.' },
  alterada: { rotulo: 'Alterada', descricao: 'Alterada na definitiva' },
  reativada: { rotulo: 'Reativada', descricao: 'Carga que estava cancelada e voltou na definitiva' },
  cancelada: { rotulo: 'Cancelada', descricao: 'Carga retirada da programação definitiva.' }
};

export const descreverSituacaoDefinitiva = registro => {
  const situacao = registro.cancelado ? 'cancelada' : registro.situacao_definitiva;
  const config = SITUACOES_DEFINITIVA[situacao];
  if (!config) return null;
  const alteracoes = (registro.alteracoesDefinitiva || [])
    .map(alteracao => `${alteracao.titulo}: ${alteracao.anterior || 'vazio'} → ${alteracao.novo || 'vazio'}`);
  return { situacao, rotulo: config.rotulo, descricao: alteracoes.length ? `${config.descricao}: ${alteracoes.join('; ')}` : config.descricao };
};

const renderCtCell = () => ({ getValue, row }) => {
  const marcacao = descreverSituacaoDefinitiva(row.original);

  return (
    <CtCell $cancelada={marcacao?.situacao === 'cancelada'}>
      <TruncatedText value={getValue() || '-'} maxLength={14} />
      {marcacao ? (
        <Tooltip dir="top" text={marcacao.descricao}>
          <SituacaoBadge $situacao={marcacao.situacao} aria-label={marcacao.descricao}>{marcacao.rotulo}</SituacaoBadge>
        </Tooltip>
      ) : null}
    </CtCell>
  );
};

// Protocolo: checkbox como o do terceiro; marcado, a linha fica verde.
const renderProtocoloCell = inlineEditConfig => ({ row }) => {
  const registro = row.original;
  const marcado = Boolean(registro.protocolo);
  const salvando = Boolean(inlineEditConfig?.protocoloSalvandoIds?.includes(registro.id));
  const descricao = marcado ? `Carga ${registro.ct || ''} marcada como protocolo. Clique para desmarcar.` : `Marcar a carga ${registro.ct || ''} como protocolo`;

  return (
    <ProtocoloLabel title={descricao} $marcado={marcado} onClick={event => event.stopPropagation()}>
      <input
        type="checkbox"
        checked={marcado}
        disabled={salvando || !inlineEditConfig?.toggleProtocolo}
        aria-label={descricao}
        onChange={event => inlineEditConfig?.toggleProtocolo?.(registro, event.target.checked)}
      />
      <span>{salvando ? 'Salvando…' : marcado ? 'Sim' : 'Não'}</span>
    </ProtocoloLabel>
  );
};

const renderTerceiroCell = inlineEditConfig => ({ row }) => {
  const registro = row.original;
  const marcado = Boolean(registro.terceiro);
  const salvando = Boolean(inlineEditConfig?.terceiroSalvandoIds?.includes(registro.id));
  const descricao = marcado ? `Carga ${registro.ct || ''} liberada para terceiro. Clique para remover.` : `Liberar a carga ${registro.ct || ''} para terceiro`;

  return (
    <TerceiroLabel title={descricao} $marcado={marcado} onClick={event => event.stopPropagation()}>
      <input
        type="checkbox"
        checked={marcado}
        disabled={salvando || !inlineEditConfig?.toggleTerceiro}
        aria-label={descricao}
        onChange={event => inlineEditConfig?.toggleTerceiro?.(registro, event.target.checked)}
      />
      <span>{salvando ? 'Salvando…' : marcado ? 'Liberada' : 'Não'}</span>
    </TerceiroLabel>
  );
};

export const getProgramacaoHavanColumns = (inlineEditConfig, vxConfig) => [
  {
    id: 'terceiro',
    accessorKey: 'terceiro',
    header: 'Terceiro',
    size: 110,
    enableCopy: false,
    sortingFn: (a, b) => Number(Boolean(a.original.terceiro)) - Number(Boolean(b.original.terceiro)),
    cell: renderTerceiroCell(inlineEditConfig)
  },
  {
    id: 'protocolo',
    accessorKey: 'protocolo',
    header: 'Protocolo',
    size: 110,
    enableCopy: false,
    sortingFn: (a, b) => Number(Boolean(a.original.protocolo)) - Number(Boolean(b.original.protocolo)),
    cell: renderProtocoloCell(inlineEditConfig)
  },
  { id: 'ct', accessorKey: 'ct', header: 'CT', size: 150, cell: renderCtCell() },
  { id: 'filial_codigo', accessorKey: 'filial_codigo', header: 'Nº filial', size: 95, cell: ({ getValue }) => getValue() ?? '-' },
  { id: 'filial', accessorKey: 'filial', header: 'Filial', size: 160, cell: renderText(22) },
  { id: 'volume_carga', accessorKey: 'volume_carga', header: 'Vol.', size: 80, cell: ({ getValue }) => getValue() ?? '-' },
  { id: 'nome', accessorKey: 'nome', header: 'Motorista', size: EDITABLE_COLUMN_WIDTH, cell: renderEditableCell('nome_motorista', 255, 24, inlineEditConfig) },
  { id: 'tipo_caminhao', accessorKey: 'tipo_caminhao', header: 'Tipo de caminhão', size: 165, cell: renderText(20) },
  { id: 'box', accessorKey: 'box', header: 'Box', size: 85, cell: renderText(10) },
  // Colunas de data e hora seguem o mesmo padrão: dd/mm/aaaa (data) ou dd/mm/aaaa hh:mm (data e hora), ordenadas pelo ISO.
  // "H.CHEG VEICULO" da consolidada (na prévia, "Início Carregamento").
  { id: 'h_chegada_veiculo', accessorFn: registro => (registro.inicio_carregamento ? chaveDataHora(diaDoCarregamento(registro), registro.inicio_carregamento) : ''), header: 'H. cheg. veículo', size: 150, cell: renderChegadaVeiculo(), copyValue: ({ row }) => descreverChegadaVeiculo(row.original) },
  { id: 'saida_cdh', accessorFn: registro => chaveDataHora(registro.saida_cdh, registro.termino_carregamento), header: 'Saída CDH', size: 150, cell: renderSaidaCdh(), copyValue: ({ row }) => descreverSaidaCdh(row.original) },
  { id: 'chegada_loja', accessorKey: 'chegada_loja', header: 'Chegada loja', size: 120, cell: renderData(), copyValue: ({ getValue }) => formatarDataBr(getValue()) },
  { id: 'numero_frota', accessorKey: 'numero_frota', header: 'Frota', size: EDITABLE_COLUMN_WIDTH, cell: renderEditableCell('numero_frota', 100, 14, inlineEditConfig) },
  { id: 'placa_cavalo', accessorKey: 'placa_cavalo', header: 'Placa cavalo', size: EDITABLE_COLUMN_WIDTH, cell: renderEditableCell('placa_cavalo', 10, 12, inlineEditConfig) },
  { id: 'placa_carreta_1', accessorKey: 'placa_carreta_1', header: 'Carreta 1', size: EDITABLE_COLUMN_WIDTH, cell: renderEditableCell('placa_carreta_1', 10, 22, inlineEditConfig), copyValue: ({ row }) => descreverCarreta(row.original, 'placa_carreta_1') },
  { id: 'placa_carreta_2', accessorKey: 'placa_carreta_2', header: 'Carreta 2', size: EDITABLE_COLUMN_WIDTH, cell: renderEditableCell('placa_carreta_2', 10, 22, inlineEditConfig), copyValue: ({ row }) => descreverCarreta(row.original, 'placa_carreta_2') },
  { id: 'placa_carreta_3', accessorKey: 'placa_carreta_3', header: 'Carreta 3', size: EDITABLE_COLUMN_WIDTH, cell: renderEditableCell('placa_carreta_3', 10, 22, inlineEditConfig), copyValue: ({ row }) => descreverCarreta(row.original, 'placa_carreta_3') },
  { id: 'vx', header: 'VX', size: 230, enableSorting: false, enableCopy: false, cell: renderVxCell(vxConfig) },
  { id: 'gestor', accessorFn: registro => registro.gestor_nome || '', header: 'Gestor', size: 150, enableCopy: false, cell: renderGestorCell(inlineEditConfig) },
  { id: 'observacao_operador', accessorKey: 'observacao_operador', header: 'Observação', size: 240, cell: renderObservacaoCell(inlineEditConfig) }
];

const GestorButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 130px;
  min-height: 20px;
  padding: 1px 7px 1px 5px;
  border: 1px ${({ $pessoal }) => ($pessoal ? 'dashed #94a3b8' : 'solid #cbd8e6')};
  border-radius: 4px;
  background: #ffffff;
  color: #334155;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;

  svg {
    flex-shrink: 0;
    color: #1faa59;
    font-size: 14px;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover:not(:disabled) {
    border-color: #1faa59;
  }

  &:disabled {
    color: #8a96a3;
    font-weight: 500;
    cursor: default;

    svg { color: #a8b4c2; }
  }
`;

const VxButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 20px;
  max-width: 190px;
  border: 1px solid ${({ $consultada, $andamento }) => ($consultada ? '#cbd8e6' : $andamento ? '#e0b44c' : '#163455')};
  border-radius: ${({ $andamento }) => ($andamento ? '999px' : '4px')};
  background: ${({ $consultada, $andamento }) => ($consultada ? '#f1f5f9' : $andamento ? '#fff8e6' : '#ffffff')};
  color: ${({ $consultada, $andamento }) => ($consultada ? '#526275' : $andamento ? '#8a5a00' : '#163455')};
  padding: ${({ $andamento }) => ($andamento ? '1px 10px 1px 7px' : '2px 7px')};
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
    font-size: 14px;
  }

  &:hover:not(:disabled) {
    border-color: ${({ $andamento }) => ($andamento ? '#c99200' : '#163455')};
    background: ${({ $andamento }) => ($andamento ? '#fff1cc' : '#f5f8fb')};
  }

  &:disabled { cursor: default; }
`;

const VxTexto = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VxCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const VxAlertIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: #d99700;
  font-size: 20px;
  cursor: pointer;
`;

const VxLaudoButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 20px;
  padding: 1px 6px;
  flex-shrink: 0;
  border: 1px solid #cbd8e6;
  border-radius: 4px;
  background: #ffffff;
  color: #c62828;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;

  svg {
    margin-right: 3px;
    font-size: 14px;
  }

  &:hover {
    border-color: #c62828;
    color: #9f1d1d;
  }
`;

const ProtocoloLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 1px 7px;
  border: 1px solid ${({ $marcado }) => ($marcado ? '#7e22ce' : '#cbd8e6')};
  border-radius: 999px;
  background: ${({ $marcado }) => ($marcado ? '#7e22ce' : '#ffffff')};
  color: ${({ $marcado }) => ($marcado ? '#ffffff' : '#526275')};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;

  input {
    width: 13px;
    height: 13px;
    margin: 0;
    accent-color: #7e22ce;
    cursor: pointer;
  }

  input:disabled {
    cursor: progress;
  }
`;

const TerceiroLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 1px 7px;
  border: 1px solid ${({ $marcado }) => ($marcado ? '#c2410c' : '#cbd8e6')};
  border-radius: 999px;
  background: ${({ $marcado }) => ($marcado ? '#c2410c' : '#ffffff')};
  color: ${({ $marcado }) => ($marcado ? '#ffffff' : '#526275')};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;

  input {
    width: 13px;
    height: 13px;
    margin: 0;
    accent-color: #c2410c;
    cursor: pointer;
  }

  input:disabled {
    cursor: progress;
  }
`;

const CORES_SITUACAO = {
  nova: { fundo: '#e8f6ec', borda: '#9fd3ad', texto: '#1f5c30' },
  alterada: { fundo: '#e8f1fb', borda: '#9ec3e6', texto: '#163455' },
  reativada: { fundo: '#f3eefc', borda: '#c6b3e6', texto: '#4b2a7a' },
  cancelada: { fundo: '#fdecee', borda: '#f0a7ad', texto: '#a61e2a' }
};

const CtCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: ${({ $cancelada }) => ($cancelada ? 'line-through' : 'none')};
  color: ${({ $cancelada }) => ($cancelada ? '#8a96a3' : 'inherit')};
`;

const SituacaoBadge = styled.span`
  display: inline-block;
  padding: 1px 6px;
  border: 1px solid ${({ $situacao }) => CORES_SITUACAO[$situacao].borda};
  border-radius: 999px;
  background: ${({ $situacao }) => CORES_SITUACAO[$situacao].fundo};
  color: ${({ $situacao }) => CORES_SITUACAO[$situacao].texto};
  font-size: 10px;
  font-weight: 700;
  line-height: 1.4;
  text-decoration: none;
  cursor: help;
`;

const DataCelula = styled.span`
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const SugestaoVeiculo = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const Selo = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 6px;
  border: 1px solid #e3e8ef;
  border-radius: 9px;
  background: #f8fafc;
  color: #52627a;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
`;

const IconePatio = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1px solid ${({ $cor }) => `${$cor}33`};
  border-radius: 50%;
  background: ${({ $fundo }) => $fundo};
  color: ${({ $cor }) => $cor};
  cursor: help;
  transition: transform 0.12s ease;

  svg { font-size: 12px; }
  &:hover { transform: scale(1.15); }
`;

const SeloTexto = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 7px;
  border: 1px solid ${({ $borda }) => $borda};
  border-radius: 9px;
  background: ${({ $fundo }) => $fundo};
  color: ${({ $cor }) => $cor};
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
`;

const Selos = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const QuadradoPlotagem = styled.span`
  flex: none;
  display: block;
  width: 18px;
  height: 18px;
  border-radius: 3px;
  background: ${({ $cor }) => $cor};
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.08);
`;

// Logo já vem recortada com o fundo da marca (altura 2x para tela retina).
const LogoSelo = styled.img`
  flex: none;
  display: block;
  height: 18px;
  width: auto;
  max-width: 64px;
  border-radius: 3px;
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.08);
`;

const CelulaEditavel = styled.span`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
`;

export const COR_FORA_TMS = '#c2410c';

const ForaTmsBadge = styled.span`
  flex-shrink: 0;
  padding: 1px 6px;
  border: 1px solid #fdba74;
  border-radius: 999px;
  background: #fff7ed;
  color: ${COR_FORA_TMS};
  font-size: 10px;
  font-weight: 700;
  line-height: 1.4;
  white-space: nowrap;
  cursor: ${({ $clicavel }) => ($clicavel ? 'pointer' : 'help')};
  &:hover { border-color: ${({ $clicavel }) => ($clicavel ? COR_FORA_TMS : '#fdba74')}; }
  &:focus-visible { outline: 2px solid #fdba74; outline-offset: 1px; }
`;

const EnviadoTmsBadge = styled.span`
  flex-shrink: 0;
  padding: 1px 6px;
  border: 1px solid #9ec3e6;
  border-radius: 999px;
  background: #eef5fc;
  color: #1d4f86;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.4;
  white-space: nowrap;
  cursor: help;
`;
